import React, { useState, useEffect } from 'react';
import {
  faEdit,
  faLightbulbSlash,
  faPhoneSquareAlt,
  faPhone,
  faEnvelope
} from '@fortawesome/pro-solid-svg-icons';
import { FormInstance } from '@lightbeamhealth/blueprint-form';

import Icon from '../../atoms/icon';
import { Tabs, Tab } from '../../molecules/tab';
import Card, { CardFrame } from '../../organisms/card';
import Modal from '../../organisms/modal';
import Toast from '../../organisms/toast';
import Callout from '../../organisms/callout';
import { useMetaData } from '../../../contexts/metadata';
import { FormatPhone } from '../../../libs/format';
import { PostJSON } from '../../../libs/request';
import { useTriagePatient } from '../../../contexts/triage/patient';
import { GetSurveyResponse } from '../../../libs/survey';
import QualitySurveyForm from './survey-form';
import { ISurveyQual } from '../../../libs/apps/surveyqual';

interface EditSurveyProps {
  survey?: any;
  routes: {
    call: string;
    message: string;
  };
  setSurvey: (survey: ISurveyQual) => void;
  setSurveyType: (surveyType: ISurveyQual['type']) => void;
}

const EditSurvey: React.FC<EditSurveyProps> = ({
  survey,
  routes,
  setSurvey,
  setSurveyType
}): JSX.Element => {
  const { terms } = useMetaData();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [surveyForm, setSurveyForm] = useState<FormInstance<any>>(null);
  const {
    patientData: { patient }
  } = useTriagePatient();

  const updateSurvey = (data: any) => {
    const params = {
      patientId: patient.id,
      isEditing: true,
      survey: {
        ...data
      }
    };
    setIsLoading(true);
    PostJSON<GetSurveyResponse>({
      url: '/survey/edit/',
      params
    }).then((response) => {
      setIsLoading(false);
      if (response.status !== 'OK') {
        Modal.error({
          title: 'Error Updating Survey',
          content: response.message
        });
        return;
      }
      Toast({
        intent: 'success',
        message: 'The survey has been updated successfully!'
      });
      if ((response as GetSurveyResponse).survey) {
        const survey = (response as GetSurveyResponse).survey;
        setSurvey(survey);
        setSurveyType(survey.type);
      }
    });
  };

  useEffect(() => {
    if (surveyForm && survey) {
      surveyForm.setData(survey);
    }
  }, [survey, surveyForm]);

  return (
    <Tabs className="mb-4" id="Prescription" vertical>
      {survey == null ? (
        <Tab
          id="Survey"
          title={
            <span>
              <Icon icon={faEdit} className="mr-4" /> Survey
            </span>
          }
          panel={
            <Card>
              <h4>
                <Icon icon={faLightbulbSlash} className="mr-2" /> Survey Not
                Found
              </h4>
              <p>There is no survey data available at this time.</p>
            </Card>
          }
        />
      ) : (
        <Tab
          id="Survey"
          title={
            <span>
              <Icon icon={faEdit} className="mr-4" /> Survey
            </span>
          }
          panel={
            <>
              <CardFrame>
                <QualitySurveyForm
                  setForm={setSurveyForm}
                  isDisabled={isLoading}
                  onSubmit={updateSurvey}
                />
              </CardFrame>
            </>
          }
        />
      )}
      {routes ? (
        <Tab
          id="Routes"
          title={
            <span>
              <Icon icon={faPhoneSquareAlt} className="mr-4" /> Phones
            </span>
          }
          panel={
            <Card>
              <Callout>
                The following are phone numbers used by CareSignal to
                communicate with the {terms.patient.default} for this survey.
              </Callout>
              <Card className="mt-4">
                <Icon icon={faPhone} /> Automated Calls:{' '}
                <b>{FormatPhone(routes.call, true)}</b>
              </Card>
              <Card className="mt-4">
                <Icon icon={faEnvelope} /> Text Messages:{' '}
                <b>{FormatPhone(routes.message, true)}</b>
              </Card>
              <Callout className="mt-4" intent="primary">
                You may ask the {terms.patient.default} to save the phone
                numbers in their contacts to reduce the chance of the
                calls/messages being mistaken as SPAM.
              </Callout>
            </Card>
          }
        />
      ) : null}
    </Tabs>
  );
};

export default EditSurvey;
